import { useCallback, useMemo, useState } from 'react';
import { CalendarRange, ChevronRight, Flame, SquareStack } from 'lucide-react';

import { ROI_DEMO_DATASET } from '../data/roi-demo-dataset';
import type { RoiSignalSource } from '../types/roi';
import { deriveDataset } from '../utils/derive-dataset';
import { RoiAssumptionsPanel } from './RoiAssumptionsPanel';
import { RoiAuditLog } from './RoiAuditLog';
import { RoiHeroSection } from './RoiHeroSection';
import { RoiIntegrationToggleBar } from './RoiIntegrationToggleBar';
import { RoiReworkFormula } from './RoiReworkFormula';

const ALL_SOURCES: ReadonlyArray<RoiSignalSource> =
  ROI_DEMO_DATASET.integrationCoverage.steps.map((step) => step.source);
const FULL_RECALL_PCT = Math.round(
  (ROI_DEMO_DATASET.caughtProjects / ROI_DEMO_DATASET.totalProjects) * 100
);

export function RoiDashboardPage() {
  const [enabledSources, setEnabledSources] = useState<Set<RoiSignalSource>>(
    () => new Set(ALL_SOURCES)
  );

  const toggleSource = useCallback((source: RoiSignalSource) => {
    setEnabledSources((previous) => {
      const next = new Set(previous);
      if (next.has(source)) {
        next.delete(source);
      } else {
        next.add(source);
      }
      return next;
    });
  }, []);

  const derivedDataset = useMemo(
    () => deriveDataset(ROI_DEMO_DATASET, enabledSources),
    [enabledSources]
  );

  const currentRecallPct = Math.round(
    (derivedDataset.caughtProjects / derivedDataset.totalProjects) * 100
  );

  return (
    <div className="min-h-screen w-full bg-page-bg">
      <header className="sticky top-0 z-30 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-page-bg/90 px-6 py-2 backdrop-blur md:px-8">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
          <SquareStack className="h-3.5 w-3.5 text-slate-400" />
          <span>Insights</span>
          <ChevronRight className="h-3 w-3 text-slate-300" />
          <span className="text-slate-900">ROI &amp; cost recapture</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="hidden items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-bold tabular-nums uppercase tracking-[0.1em] text-red-700 sm:inline-flex">
            <Flame className="h-3 w-3" />
            Money is leaking now
          </span>
          <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1 shadow-[0_1px_0_rgba(15,23,42,0.04)]">
            <CalendarRange className="h-3.5 w-3.5 text-slate-400" />
            <span className="font-medium text-slate-700">
              {ROI_DEMO_DATASET.assumptions.timeWindowLabel}
            </span>
          </div>
        </div>
      </header>

      <main className="flex w-full flex-col gap-4 px-6 py-5 pb-24 md:px-8">
        <RoiHeroSection
          dataset={derivedDataset}
          fullCoverageHeroUsd={ROI_DEMO_DATASET.heroTotalUsd}
        />

        <RoiAuditLog dataset={derivedDataset} />

        <RoiIntegrationToggleBar
          coverage={ROI_DEMO_DATASET.integrationCoverage}
          enabledSources={enabledSources}
          onToggle={toggleSource}
          currentRecallPct={currentRecallPct}
          fullRecallPct={FULL_RECALL_PCT}
        />

        <RoiReworkFormula formula={derivedDataset.buckets.rework.formula} />

        <RoiAssumptionsPanel dataset={derivedDataset} />
      </main>
    </div>
  );
}
